// Copyright: IQGeo Limited 2010-2019
import myw from 'myWorld/base/core';

myw.UsageMonitor = myw.MywClass.extend({
    includes: myw.Events,

    /**
     * @class Provides an api to track client usage. </br>
     * Usage stats are collected for the life of the session (1 day) or logout before being reset. </br>
     * Stats are serialised/deserialised into localstorage between page refreshes and transmission. </br>
     * @param   {System}  system
     * @extends {MywClass}
     * @constructs
     */
    initialize(system) {
        this.name = 'usageMonitor';

        this.system = system;

        this.levels = { licence: 0, functionality: 1, data: 2 };

        this.config = null; //default config from server

        this.session = null; //current users usage session

        this.actions = null; //operations performed during the seesion

        this.dirty = false; //A flag for indicating if we have actions to send

        //ENH: refactor this to an async method
        this.initialized = new Promise((resolve, reject) =>
            myw.currentUser ? resolve() : reject(new Error('No current user'))
        )
            .then(system.getUsageMonitorSettings.bind(system))
            .then(config => {
                // Get configuration
                if (!config) return Promise.reject(new Error('No config defined'));

                this.config = config;
                myw.trace(this.name, 1, 'Usage Monitor config:', JSON.stringify(config));
                return config;
            })
            .then(config => {
                //Get usageMonitor state from localstorage
                if (config.active) {
                    return this.system.getSavedSharedState();
                } else {
                    myw.trace(this.name, 1, 'Usage Monitor disabled by configuration');
                }
            })
            .then(state => {
                if (!state) return true;
                const { usageMonitor } = state;
                //EHN check expiry value from config
                if (
                    usageMonitor &&
                    usageMonitor.session &&
                    Date.now() < usageMonitor.session.expiry
                ) {
                    myw.trace(this.name, 1, 'Using existing session');
                    this.session = usageMonitor.session;
                    this.actions = usageMonitor.actions || {};
                    return;
                }

                this.actions = (usageMonitor && usageMonitor.actions) || {};
                this._createSession();
                return false;
            })
            .then(disabled => {
                //Register timer to post data periodically
                if (!disabled) this._registerHandlers();
            });

        this.initialized.catch(e => {
            console.log(`Usage Monitor Disabled: ${e.message}`);
            if (!['No current user', 'No config defined'].includes(e.message)) console.log(e.stack);
        });
    },

    _registerHandlers() {
        this.sendTimer = setInterval(
            this._sendUsage.bind(this),
            this.config.update_interval_mins * 60000
        );

        if (window) {
            window.onbeforeunload = this._sendUsage.bind(this);
        }
    },

    _sendUsage() {
        const { session, actions } = this;

        if (!this.dirty) return;
        this.dirty = false;

        myw.trace(this.name, 1, 'Posting actions to server:', JSON.stringify(actions));

        if (Date.now() > session.expiry) {
            myw.trace(this.name, 1, 'Session resolution expired');
            //save current values for current session
            this.system.updateUsageMonitorSession(session.id, actions);

            //start new session
            this._createSession();
            return;
        }

        this.system
            .updateUsageMonitorSession(session.id, actions)
            .then(() => this.fire('usageMonitor-persist', true))
            .then(() => myw.trace(this.name, 1, 'Post complete'));
    },

    /**
     * Record usage
     * @param  {string|number} levels
     * @param  {string} action
     */
    log(level, application, action) {
        this.initialized
            .then(() => {
                const validLevel = this.getLevel(level);
                if (validLevel != null && validLevel <= this.config.level)
                    this._appendAction(application, `${level}.${action}`);
                this.dirty = true;
            })
            .catch(() => {}); //not initialized - ignore
    },

    /**
     * Lookup a level by name or value
     * @param  {string|number} level
     * @return {number}
     */
    getLevel(level) {
        if (isNaN(level)) {
            const selected = this.levels[level];
            if (null != selected) return selected;
            throw new Error('level not defined');
        }

        for (const key in this.levels) {
            const current = this.levels[key];
            if (current === parseInt(level)) return current;
        }

        throw new Error('usage level not defined');
    },

    /**
     * Called when application closes
     * @return {object}
     */
    getState() {
        const { session, actions } = this;
        return { session, actions };
    },

    _appendAction(applicationName, action) {
        let application = this.actions[applicationName] || {};
        let count = application[action] || 0;
        count++;
        application[action] = count;
        this.actions[applicationName] = application;

        const notification = { applicationName, action, count };
        this.fire('usageMonitor-log', notification);
        myw.trace(this.name, 1, 'new operation:', JSON.stringify(notification));
    },

    _createSession() {
        myw.trace(this.name, 1, 'creating session');
        const now = new Date();
        const expiry = new Date();
        expiry.setSeconds(expiry.getSeconds() + this.config.resolution_hours * 60 * 60);

        const sessionDef = {
            created_at: now.getTime(),
            expiry: expiry.getTime(),
            client: this._getBrowser()
        };

        this.actions = {};
        // include core license usage on the new session
        if (myw.app) this.actions[myw.app.name] = { 'licence.core': 1 };

        return this.system.createUsageMonitorSession(sessionDef).then(session => {
            const merged = { id: session.id, ...sessionDef };
            this.session = merged;
            myw.trace(this.name, 1, 'New session created', JSON.stringify(merged));
            return merged;
        });
    },

    _getBrowser() {
        /* globals L */
        if (typeof L == 'undefined') return 'Unknown';
        if (L.Browser.ie) return 'IE';
        if (L.Browser.edge) return 'Edge';
        if (L.Browser.chrome) return 'Chrome';
        if (L.Browser.safari) return 'Safari';
        if (L.Browser.gecko) return 'Firefox';
        return 'Unknown';
    }
});

export default myw.UsageMonitor;
